import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface EvidenceRow {
  readonly evidenceId: string;
  readonly evidenceType: string;
  readonly sourceModule: string | null;
  readonly documentId: string | null;
  /** The document, named by the server (G18). */
  readonly documentCode: string | null;
  readonly documentName: string | null;
  readonly contentHash: string;
  readonly capturedAt: string;
}

const EVIDENCE_TYPES = ['', 'GPS', 'PHOTO', 'VIDEO', 'AUDIT', 'APPROVAL', 'PAYMENT', 'COMPLIANCE'];

export function M23EvidenceRepositoryPage(): ReactNode {
  const { t: tr } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const [evidenceType, setEvidenceType] = useState('');
  const [rows, setRows] = useState<EvidenceRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  function load(): void {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (evidenceType) params.set('evidenceType', evidenceType);
    const qs = params.toString();
    apiRequest<{ data: EvidenceRow[] }>(`/v1/evidence${qs ? `?${qs}` : ''}`, { token })
      .then((r) => setRows(r.data))
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  return (
    <main aria-label="evidence-repository">
      <h1>{tr('m23.evidence.title')}</h1>
      <form aria-label="evidence-filter-form" onSubmit={(e) => { e.preventDefault(); load(); }}>
        <label htmlFor="evidenceType">{tr('m23.evidence.evidenceType')}</label>
        <select id="evidenceType" value={evidenceType} onChange={(e) => setEvidenceType(e.target.value)}>
          {EVIDENCE_TYPES.map((t) => <option key={t} value={t}>{t || tr('m23.evidence.all')}</option>)}
        </select>
        <button type="submit">{tr('m23.evidence.applyFilter')}</button>
      </form>
      {error && <p role="alert">{error}</p>}
      {loading && <p>{tr('m23.evidence.loading')}</p>}
      {rows && (
        <table aria-label="evidence-table">
          <thead><tr><th>{tr('m23.evidence.colType')}</th><th>{tr('m23.evidence.colSourceModule')}</th><th>{tr('m23.evidence.colDocument')}</th><th>{tr('m23.evidence.colContentHash')}</th><th>{tr('m23.evidence.colCapturedAt')}</th></tr></thead>
          <tbody>
            {rows.map((ev) => (
              <tr key={ev.evidenceId}>
                <td>{ev.evidenceType}</td>
                <td>{ev.sourceModule ?? '—'}</td>
                <td>{ev.documentCode ? `${ev.documentCode} — ${ev.documentName ?? ''}` : '—'}</td>
                <td>{ev.contentHash}</td>
                <td>{at(ev.capturedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
